import { Box, Stack, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { isTranslationKey, resolveDisplayText, resolveEmbeddedKeys } from "modules/record/shared/services/recordMetadataLabels";
import { BuiltFormSection } from "../services/formLayoutBuilder";
import { FieldRuntimeProps } from "../types/record-producer-types";
import { FieldFactory } from "./FieldFactory";

// `tableLabel` alimenta o {{table}} de títulos como "Geral de {{table}}".
export function resolveSectionTitle(t: TFunction, label: string, tableLabel?: string): string | null {
    const resolved = resolveDisplayText(t, label, tableLabel ? { table: tableLabel } : undefined);
    return typeof resolved === "string" && resolved && !isTranslationKey(resolved) ? resolveEmbeddedKeys(t, resolved) : null;
}

export const TWO_COLUMN_GRID_SX = {
    display: "grid",
    gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
    columnGap: { xs: 1.5, sm: 2 },
    rowGap: { xs: 1.5, sm: 2 },
    alignItems: "start",
};

export function RecordFormSection({ section, runtimeStates, hideTitle = false, tableLabel, embedded = false }: {
    section: BuiltFormSection;
    runtimeStates: Record<string, FieldRuntimeProps>;
    hideTitle?: boolean;
    tableLabel?: string;
    embedded?: boolean;
}) {
    const { t } = useTranslation();
    const title = section.kind === "other"
        ? t("form_layout.section.other_fields_title", { defaultValue: "Other fields" })
        : resolveSectionTitle(t, section.label, tableLabel);
    // `show_label = false` tira o cabeçalho (e o espaço dele), mas a seção mantém o nome acessível.
    // Em abas a própria aba mostra o título, então o ocultamento não se aplica (hideTitle).
    const hiddenTitle = !hideTitle && section.showLabel === false && section.kind !== "other";
    return <Stack spacing={{ xs: 1.5, sm: 2 }} {...(hiddenTitle && title ? { role: "group", "aria-label": title } : {})}>
        {!hideTitle && !hiddenTitle && title && <Typography component="h2" variant="h6">{title}</Typography>}
        {section.twoColumns
            ? <Box sx={embedded ? { ...TWO_COLUMN_GRID_SX, gridTemplateColumns: "minmax(0, 1fr)",
                "@container record-producer (min-width: 680px)": { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" } } : TWO_COLUMN_GRID_SX}>
                {section.twoColumns.map((columnFields, index) =>
                    <Stack key={index} spacing={{ xs: 1.5, sm: 2 }} data-testid="form-section-column">
                        {columnFields.map(column => <FieldFactory key={column.id} column={column} runtimeProps={runtimeStates[column.name]} />)}
                    </Stack>
                )}
            </Box>
            : section.columns.map(column => <FieldFactory key={column.id} column={column} runtimeProps={runtimeStates[column.name]} />)}
    </Stack>;
}
